import { Box, Button, Card, CardContent, CardHeader, Divider, FormHelperText, Grid, 
  MenuItem, TextField as MuiTextField, Typography, useTheme } from '@mui/material';
import { Dispatch, FC, useEffect, useReducer } from 'react'
import { MfaWay, OptionResponseDto, UserResponseDto } from '@/gen/api/user';
import * as Yup from 'yup'
// import PhoneInput from 'react-phone-number-input'
// import CustomPhoneNumber from '@/components/phoneInput/phoneInput'
// import 'react-phone-number-input/style.css';
import { useFormik, useFormikContext } from "formik";
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import SelectField from '../formik/SelectField';
import { languageOptions } from '@/utils/lang-options';
import { useGetMainList, useSaveContext } from 'ra-core';
import { pages, sorts } from '@/utils/paramsWithGetMainList';
import { RecPatch } from '@/dataProvider/tools';
import { ProfileFormValues } from '@/pages/profile/Create';
import { Action, reducer, RequestRecipt, States, useSendCodeCountDown } from './ChangePhone.hook';
import Create from '@/controller/Create';
import { useSimpleSaveAction } from '@/controller/SaveContextProvider';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';
import useIdTokenUser from '@/authProvider/useIdTokenUser';
import retrieveAxiosErr from '@/utils/retrieveAxiosErr';


const initialValues = {
  submit: '',
  firstName: '',
  lastName: '',
  phone: '',
  language: 'en-US', // navigator.language,
  timezone: '' as '' | OptionResponseDto,
}

export type FormValues = typeof initialValues

export type InnerFormExtendProps = { 
  user?: UserResponseDto, 
  save: (record: Partial<ProfileFormValues>, callbacks?: any)=>void,
}

const validationSchema =
  Yup.object().shape({
    firstName: Yup.string().max(50).required('First Name is required'),
    lastName:  Yup.string().max(50).required('Last Name is required'),
    phone: Yup.string().max(50)
      .matches(/^[\+]?[1-9]\d{1,14}$/, 'Invalid phone number')
      .required('Change Phone Number is required')
  })
  
const res_veri_code = 'phone-veri-code'

export const ProfileCreateForm: FC<InnerFormExtendProps> = (props) => {
  const {
    save,
    user,
  } = props
  const idTokenUser = useIdTokenUser()

  const { data: timezoneBag } = useGetMainList<OptionResponseDto & RecPatch>('timezone-options', pages(), sorts(), {})
  const timezoneOptions = Object.values(timezoneBag || {}).sort((a, b) => { return a.value !== b.value ? ((+(a.value ?? 0)) - (+(b.value ?? 0))) : ((a.name ?? '') < (b.name ?? '') ? -1 : 1)  })

  const [states, dispatch] = useReducer(reducer, {
    requested: null as RequestRecipt | null,
    verified: false,
    vCode: '',
    open: false,
  })
  
  const initVal: FormValues = { ...initialValues, phone: user?.phone || '' }

  const onSubmit: OnSubmit<FormValues> = (values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const { firstName, lastName, language, timezone } = values
    const profile: Partial<ProfileFormValues> = {
      firstName,
      lastName,
      phone: states.requested?.phone,
      phoneVerifyCode: states.vCode,
      language,
      timeZoneName: timezone !== '' ? timezone?.name : undefined,
      ...({
        timeZoneOffset: timezone !== '' ? timezone?.value : undefined,
        username: idTokenUser?.email,
      } as any),
      phoneVerifyCodeId: states.requested?.vCodeId,
      mfaWay: MfaWay.Phone,
    }

    save(profile, options)
  }

  const formikCtrl = useFormik({ initialValues: initVal, onSubmit, validationSchema })
  const { errors, touched, isSubmitting, values, setFieldValue, setFormikState } = formikCtrl

  useEffect(() => {
    if (!touched.firstName && user?.firstName) {
      setFieldValue('firstName', user.firstName)
    }
    if (!touched.lastName && user?.lastName) {
      setFieldValue('lastName', user.lastName)
    }
  }, [touched.firstName, touched.lastName, user, setFieldValue])

  useEffect(() => {

    if (timezoneOptions.length > 0) {
      const offset = ((n) => `${n < Math.abs(n) || n === 0 ? '' : '-'}${Math.abs(n)}`)(new Date().getTimezoneOffset());
      const timezoneName = getStandardTimezoneName()
      const currTimezone = 
        timezoneOptions.find(t => t.name === timezoneName) ??
        timezoneOptions.find(t => t.value === offset)
      currTimezone && setFieldValue('timezone', currTimezone)
    }
  }, [timezoneOptions.length, setFieldValue])


  const createHandlers = useDefaultSavedHandlers("Send code")
  return (
    <OdForm formikCtrl={formikCtrl} {...{role: "application"}}>

      <Card>
        <CardHeader title="Profile" />
        <Divider />
        <CardContent>
          <Grid
            container
            spacing={4}
          >
            <Grid item md={6} xs={12} >
              <TextField label="First Name*" name="firstName" />
            </Grid>
            <Grid item md={6} xs={12} >
              <TextField label="Last Name*" name="lastName" />
            </Grid>

            <Grid item md={6} xs={12} >
              <MuiTextField
                id="email"
                fullWidth
                disabled
                label="Email Address"
                name="email"
                type="email"
                value={idTokenUser?.email || ''}
                variant="outlined"
              />
            </Grid>
            <Grid item md={6} sx={{display: {xs: 'none', md: 'block'}}} />

            <Grid item md={6} xs={12} >
              <SelectField
                label="Preferred Language"
                name="language"
              >
                {languageOptions.map(l => (
                  <MenuItem key={l.tag} value={l.tag} >{l.name}</MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid item md={6} xs={12} >
              <SelectField
                label="Time Zone"
                name="timezone"
              >
                {timezoneOptions.map(t => (
                  <MenuItem key={t.id} value={t as any} >{t.displayName}</MenuItem>
                ))}
              </SelectField>
            </Grid>

            <Create resource={res_veri_code} basePath="" {...createHandlers}>
              <CollectPhone {...{states, dispatch}} />
            </Create>

            {!!states.requested &&
              <>
                <Grid item md={12} >
                  <Typography color="textSecondary" variant="body2" >
                    Please enter the verification code we sent to {values.phone} via SMS.
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12} >
                  <MuiTextField
                    id="vcode1"
                    fullWidth
                    label="Verification Code"
                    name="vcode"
                    onChange={(e) => {
                      setFormikState(v => {
                        const t = v.touched
                        if (t.firstName && t.lastName) { return v }
                        return { ...v, touched: { ...t, firstName: true, lastName: true } }
                      })
                      dispatch(({kind:'vCode', vCode: e.target.value}))
                    }}
                    value={states.vCode}
                    variant="outlined"
                  />
                </Grid>
              </>}

          </Grid>
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {errors.submit}
              </FormHelperText>
            </Box>
          )}
        </CardContent>
        {!!states.requested &&
          <>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }} >
            <Button
              color="primary"
              disabled={
                isSubmitting 
                || !!errors.firstName
                || !!errors.lastName
                || !!errors.phone
                || !states.vCode
              }
              type="submit"
              variant="contained"
            >
              Verify
            </Button>
          </Box>
          </>
        }
      </Card>

    </OdForm>
  )
}


const CollectPhone : FC<{states: States, dispatch: Dispatch<Action>}> = ({
  states, dispatch
}) => {
  const theme = useTheme()

  const {
    states: {innerStates},
    clearCountDown,
    onRequestCodeSuccess,
  } = useSendCodeCountDown(states, dispatch)
  
  const { saving: requesting } = useSaveContext()
  const requestCode = useSimpleSaveAction()
  
  const { isSubmitting, values, errors, setFieldValue, setFieldError, } = useFormikContext<FormValues>()

  return (
    <>
      <Grid item md={12} >
        <Typography color="textSecondary" variant="body2" >
          To ensure the security of your account and your data, we
          require multi-factor authentication. Please enter your mobile
          phone number below to receive a verification code via SMS for validation.
        </Typography>
      </Grid>

      <Grid item md={6} xs={12} >
        <TextField
          disabled={!!states.requested}
          label="Phone Number*"
          name="phone"
        />
      </Grid>
      
      <Box sx={{ flex: '100%', pl: 4, pt: 3, 
        '& button': { mt: 1 }
      }}>
        <Button
          color={theme.palette.mode   === 'light' ? "inherit"   : 'primary'}
          variant={theme.palette.mode === 'light' ? "contained" : 'text'}
          disabled={isSubmitting || !values.phone || !!errors.phone || requesting || innerStates.countDown !== null}
          type="button"
          onClick={() => requestCode({phone: values.phone}, { 
            onSuccess: onRequestCodeSuccess(values.phone),
            onFailure: (err:any) => setFieldError('submit', retrieveAxiosErr(err))
          })}
          sx={{ mr: 2 }}
        >
          {!states.requested ? "Send Code" : 
            (innerStates.countDown === null ? "Resend Code" : `Resend in ${innerStates.countDown}s`)}
        </Button>
        {!!states.requested &&
          <Button
            color={theme.palette.mode === 'light' ? "inherit" : 'primary'}
            variant={theme.palette.mode === 'light' ? "contained" : 'text'}
            disabled={isSubmitting || !states.requested || !!errors.phone}
            type="button"
            onClick={() => {
              setFieldValue('phone', '')
              clearCountDown()
            }}
          >
            Change Phone Number
          </Button>}

      </Box>

    </>
  )
}

const getStandardTimezoneName = () =>
  new Date()
      .toLocaleDateString('en-US', {
        day: '2-digit',
        timeZoneName: 'long',
      })
      .slice(4)

